import { lazy, Suspense, useEffect, useState } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { gtmPageView, gtmPushEvent } from './utils/gtm';
import { fbqPageView, fbqTrackEvent } from './utils/meta';
import { ttqPageView } from './utils/tiktok';
import i18next from "i18next";
import inactivityTracker from './utils/inactivityTracker';
/// Components
import Index from './jsx/index';
/// Style
import "./css/style.css";
import "./css/custom.css";

const SignUp = lazy(() => import('./jsx/pages/Registration'));
const ForgotPassword = lazy(() => import('./jsx/pages/ForgotPassword'));
// Custom Pages
const RegisterVerifyOTP = lazy(() => import('./jsx/pages/RegisterVerifyOTP'));
const RegisterGoogleAuth = lazy(() => import('./jsx/pages/RegisterGoogleAuth'));
const ForgotGoogleAuth = lazy(() => import('./jsx/pages/ForgotGoogleAuth'));
const Sumsub = lazy(() => import('./jsx/pages/Sumsub'));
const RegisterVerifyOTPSignup = lazy(() => import('./jsx/pages/RegisterVerifyOTPSignup'));

const RegisterVerifyEmailSignup = lazy(() => import('./jsx/pages/RegisterVerifyEmailSignup'));

const VipRegisterVerifyEmailSignup = lazy(() => import('./jsx/pages/VipVerifyEmailSignup'));

// New Signup Flow
const NewRegistration = lazy(() => import('./jsx/pages/NewRegistration'));
const ChooseShareholder = lazy(() => import('./jsx/pages/ChooseShareholder'));
const PersonalRegistration = lazy(() => import('./jsx/pages/PersonalRegistration'));
const PersonalRegistrationOTP = lazy(() => import('./jsx/pages/PersonalRegistrationOTP'));
const VipRegistration = lazy(() => import('./jsx/pages/VipRegistration'));

const CorporateRegistration = lazy(() => import('./jsx/pages/CorporateRegistration'));

// const Home = lazy(() => import('./jsx/components/Dashboard/Home'));
const Error404 = lazy(() => import('./jsx/pages/Error404'));

const Login = lazy(() => {
    return new Promise(resolve => {
        setTimeout(() => resolve(import('./jsx/pages/Login')), 500);
    });
});

function App(props) {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const location = useLocation();

    // Language setup
    useEffect(() => {
        const savedLanguage = window.sessionStorage.getItem("languagesessionuser") || "en";
        i18next.changeLanguage(savedLanguage);
    }, []);

    // Inactivity tracker setup - Monitor user activity and auto-logout after 30 minutes
    useEffect(() => {
        const isAuthenticated = !!window.sessionStorage.getItem("user_jwt");
        
        if (isAuthenticated) {
            // Start tracking when user is logged in
            inactivityTracker.startTracking();
        } else {
            // Stop tracking when user is logged out
            inactivityTracker.stopTracking();
        }

        // Cleanup on unmount
        return () => {
            inactivityTracker.stopTracking();
        };
    }, [window.sessionStorage.getItem("user_jwt")]);

    // UTM and attribution tracking on app load
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        
        // Track all attribution parameters
        const attributionParams = [
            'utm_source', 'utm_medium', 'utm_campaign', 
            'utm_term', 'utm_content', 'gclid', 'fbclid', 'ttclid', 'ref'
        ];

        const capturedParams = {};
        let hasAttributionData = false;

        attributionParams.forEach(param => {
            const value = urlParams.get(param);
            if (value) {
                localStorage.setItem(param, value);
                capturedParams[param] = value;
                hasAttributionData = true;
            }
        });

        // Send attribution data to GTM if any exists
        if (hasAttributionData) {
            gtmPushEvent({
                event: 'attribution_capture',
                page_url: window.location.href,
                ...capturedParams
            });
            fbqTrackEvent({
                event: 'attribution_capture',
                page_url: window.location.href,
                ...capturedParams
            });
        }

        // Generate and store session ID if not exists
        if (!sessionStorage.getItem('session_id')) {
            sessionStorage.setItem('session_id', Date.now() + '_' + Math.random().toString(36).substr(2, 9));
        }
    }, []);

    // Track page views on route changes
    // useEffect(() => {
    //     // Small delay to ensure page is rendered
    //     const timer = setTimeout(() => {
    //         const pageCategory = getPageCategory(location.pathname);
    //         const isAuthenticated = !!window.sessionStorage.getItem("user_jwt");
            
    //         gtmPageView({
    //             page_path: location.pathname,
    //             page_title: document.title,
    //             page_category: pageCategory,
    //             authenticated: isAuthenticated,
    //             search_params: location.search
    //         });
    //         fbqPageView({
    //             page_path: location.pathname,
    //             page_title: document.title,
    //             page_category: pageCategory,
    //             authenticated: isAuthenticated,
    //             search_params: location.search
    //         });
    //         ttqPageView({
    //             page_path: location.pathname,
    //             page_title: document.title,
    //             page_category: pageCategory,
    //             authenticated: isAuthenticated,
    //             search_params: location.search
    //         });
    //     }, 100);

    //     return () => clearTimeout(timer);
    // }, [location]);

    // Helper function to categorize pages
    const getPageCategory = (pathname) => {
        if (pathname === '/' || pathname === '/login') return 'authentication';
        if (pathname.includes('register')) return 'registration';
        if (pathname.includes('verify') || pathname.includes('otp')) return 'verification';
        if (pathname.includes('forgot')) return 'password_recovery';
        if (pathname.includes('sumsub')) return 'kyc';
        if (pathname.includes('dashboard')) return 'dashboard';
        return 'other';
    };

    let routes = (
        <Switch>
            {/* <Route exact path='/' component={SignUpdemo} /> */}
            <Route exact path='/'>
            <Login setIsLoggedIn={setIsLoggedIn} />
            </Route>

            <Route exact path='/login'>
            <Login setIsLoggedIn={setIsLoggedIn} />
            </Route>
            <Route exact path='/register' component={NewRegistration} />
            <Route exact path='/choose-shareholder' component={ChooseShareholder} />
            <Route exact path='/page-forgot-password' component={ForgotPassword} />
            <Route exact path='/register-verify-otp-signup' component={RegisterVerifyOTPSignup} />
            <Route exact path='/register-verify-otp-email' component={RegisterVerifyEmailSignup} />
            <Route exact path='/vipregister-verify-otp-email' component={VipRegisterVerifyEmailSignup} />
            {/* <Route exact path='/demorequest' component={SignUpdemo} /> */}
            {/* <Route exact path='/demologin' component={Logindemo} /> */}
            {/* <Route exact path='/register-verify-otpdemo' component={RegisterVerifyOTPDemo} /> */}
            {/* <Route exact path='/register-google-authdemo' component={RegisterGoogleAuthDemo} /> */}

            <Route exact path='/old-register' component={SignUp} />
            <Route exact path='/personal-registration' component={PersonalRegistration} />
            <Route exact path='/personal-registration-otp' component={PersonalRegistrationOTP} />

            <Route exact path='/vip-registration/:referal_code' component={VipRegistration} />
            <Route exact path='/corporate-registration' component={CorporateRegistration} />
            <Route exact path="/register-google-auth">
                <RegisterGoogleAuth setIsLoggedIn={setIsLoggedIn} />
            </Route>
            <Route exact path="/sumsub">
                <Sumsub setIsLoggedIn={setIsLoggedIn} />
            </Route>
            <Route exact path="/register-verify-otp">
                <RegisterVerifyOTP setIsLoggedIn={setIsLoggedIn} />
            </Route>
            <Route exact path="/forgot-google-auth" component={ForgotGoogleAuth} />
            {!isLoggedIn && <Route exact path='*' component={Login} />}
        </Switch>
    );

    const LoadingSpinner = () => (
        <div id="preloader">
            <div className="sk-three-bounce">
                <div className="sk-child sk-bounce1"></div>
                <div className="sk-child sk-bounce2"></div>
                <div className="sk-child sk-bounce3"></div>
            </div>
        </div>
    );

    if (window.sessionStorage.getItem("user_jwt")) {
        return (
            <div className="vh-100">
                <Suspense fallback={<LoadingSpinner />}>
                    <Index />
                </Suspense>
            </div>
        );
    } else {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                {routes}
            </Suspense>
        );
    }
}

export default App;
